/* eslint-disable */
import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button, Dialog } from "../ui";
import { boardApi } from "./api";
import "./AdminBoardForm.css";

/**
 * Admin-only dialog: end a current Board / Elder member's term.
 *
 * PATCHes end_date on the existing row. The row keeps living in
 * the table so it shows up under Past with a "Served YYYY–YYYY"
 * pill. This is the "move out of board" affordance.
 */
export default function EndBoardMemberDialog({
  variant = "board", // 'board' | 'elder'
  member,
  onClose,
  onEnded,
}) {
  const todayISO = new Date().toISOString().slice(0, 10);
  const [endDate, setEndDate] = useState(todayISO);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const u = member.user || {};

  const submit = async (e) => {
    e?.preventDefault();
    if (!endDate) {
      setError("Pick an end date");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // Build a term_label like "2024–2026" from start + end so the
      // Past list reads naturally.
      const startYear = member.start_date
        ? new Date(member.start_date).getFullYear()
        : null;
      const endYear = new Date(endDate).getFullYear();
      const termLabel =
        startYear && endYear
          ? startYear === endYear
            ? `${startYear}`
            : `${startYear}–${endYear}`
          : member.term_label;
      const body = {
        end_date: endDate,
        term_label: termLabel,
      };
      if (reason.trim()) body.bio = reason.trim();
      if (variant === "elder") {
        await boardApi.patchElder(member.id, body);
      } else {
        await boardApi.patchMember(member.id, body);
      }
      onEnded && onEnded();
    } catch (err) {
      setError(err.message || "Could not end term");
    } finally {
      setBusy(false);
    }
  };

  const title =
    variant === "elder" ? "End Elder term" : "Move out of board";

  return (
    <Dialog
      open={true}
      onOpenChange={(o) => !o && onClose && onClose()}
      title={title}
      className="vsbf-modal vsbf-modal-narrow"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="vsbf-end-form"
            variant="destructive"
            loading={busy}
            disabled={busy}
          >
            Move out of board
          </Button>
        </>
      }
    >
      <form id="vsbf-end-form" onSubmit={submit} className="vsbf-form">
        <div className="vsbf-warn">
          <AlertTriangle size={16} />
          <span>
            <strong>{u.name}</strong> will move to the{" "}
            {variant === "elder" ? "Past Elders" : "Past Board"} list.
            They'll no longer appear in the current{" "}
            {variant === "elder" ? "council" : "board"}.
          </span>
        </div>

        <div className="vsbf-field">
          <label htmlFor="vsbf-end-date">End date</label>
          <input
            id="vsbf-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            max={todayISO}
            required
          />
          <p className="vsbf-helper">
            Defaults to today. Backdate if the term actually ended
            earlier.
          </p>
        </div>

        <div className="vsbf-field">
          <label htmlFor="vsbf-end-reason">Note (optional)</label>
          <textarea
            id="vsbf-end-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. End of term, resigned, relocated…"
            maxLength={500}
          />
        </div>

        {error && <div className="vsbf-error">{error}</div>}
      </form>
    </Dialog>
  );
}
